import { CommonModule } from '@angular/common';
import { Component, OnDestroy, computed, inject, input, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import type { MqttClient } from 'mqtt';
import {
  ChatListItem,
  ChatMessage,
  ChatRealtimeMessage,
  ChatService,
  ChatThread,
} from './chat.service';
import { I18nService } from '../../core/i18n.service';
import { AuthService } from '../auth/auth.service';
import { MobilePageHeaderComponent } from '../../shared/mobile-page-header/mobile-page-header.component';

interface ChatMessageViewModel extends ChatMessage {
  displayDate: string;
  displayTime: string;
  mine: boolean;
}

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, MobilePageHeaderComponent],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
})
export class ChatComponent implements OnDestroy {
  private readonly chatService = inject(ChatService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  protected readonly i18n = inject(I18nService);

  private realtimeClient: MqttClient | null = null;
  readonly embeddedDesktop = input(false);

  readonly chats = signal<ChatListItem[]>([]);
  readonly activeChat = signal<ChatThread | null>(null);
  readonly activeChatId = signal<number | null>(null);
  readonly messages = signal<ChatMessageViewModel[]>([]);
  readonly activeChatPriceLabel = signal('');
  readonly isLoadingList = signal(true);
  readonly isLoadingThread = signal(false);
  readonly isSending = signal(false);
  readonly loadError = signal('');
  readonly messageDraft = signal('');
  readonly hasOpenThread = computed(() => this.activeChatId() !== null);
  readonly isAuthenticated = this.authService.isAuthenticated;
  readonly shellTitle = computed(() =>
    this.embeddedDesktop() ? this.i18n.t('chat.title.desktop') : this.i18n.t('chat.title.mobile')
  );

  constructor() {
    void this.initialize();
  }

  ngOnDestroy(): void {
    this.realtimeClient?.end(true);
  }

  async onSelectChat(chatId: number): Promise<void> {
    if (this.activeChatId() === chatId) {
      await this.syncMobileThreadUrl(chatId);
      return;
    }

    await this.openChat(chatId);
  }

  async onCloseThread(): Promise<void> {
    this.activeChatId.set(null);
    this.activeChat.set(null);
    this.activeChatPriceLabel.set('');
    this.messages.set([]);

    if (this.embeddedDesktop()) {
      return;
    }

    await this.router.navigate(['/chat'], { replaceUrl: true });
  }

  async onMobileHeaderBack(): Promise<void> {
    if (this.hasOpenThread()) {
      await this.onCloseThread();
      return;
    }

    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    await this.router.navigate(['/']);
  }

  async onCloseSidebar(): Promise<void> {
    if (this.embeddedDesktop()) {
      await this.clearDesktopChatQuery();
      return;
    }

    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    await this.router.navigate(['/']);
  }

  onDraftChange(value: string): void {
    this.messageDraft.set(value);
  }

  async onSendMessage(): Promise<void> {
    const chatId = this.activeChatId();
    const text = this.messageDraft().trim();

    if (!chatId || !text || this.isSending()) {
      return;
    }

    this.isSending.set(true);
    this.loadError.set('');

    try {
      await firstValueFrom(this.chatService.sendMessage(chatId, text));
      this.messageDraft.set('');
      await Promise.all([this.loadMessages(chatId), this.loadChats()]);
    } catch {
      this.loadError.set(this.i18n.t('chat.sendError'));
    } finally {
      this.isSending.set(false);
    }
  }

  trackByChatId(_: number, item: ChatListItem): number {
    return item.id;
  }

  trackByMessageId(_: number, item: ChatMessageViewModel): number {
    return item.id;
  }

  chatAvatar(item: ChatListItem | ChatThread | null): string {
    if (!item?.avatar) {
      return '/static_images/ic_logo.svg';
    }

    return this.chatService.buildStorageImage(item.avatar, '600:400');
  }

  private async initialize(): Promise<void> {
    if (!this.isAuthenticated()) {
      await this.router.navigate(['/login']);
      return;
    }

    this.isLoadingList.set(true);
    this.loadError.set('');

    try {
      await this.loadChats();
      await this.handleBootstrapQuery();
      this.startRealtime();
    } catch {
      this.loadError.set(this.i18n.t('chat.loadError'));
    } finally {
      this.isLoadingList.set(false);
    }
  }

  private async handleBootstrapQuery(): Promise<void> {
    const threadId = Number(this.route.snapshot.queryParamMap.get('thread') ?? 0);
    const userId = Number(this.route.snapshot.queryParamMap.get('uId') ?? 0);
    const realtyId = Number(this.route.snapshot.queryParamMap.get('rId') ?? 0);

    if (threadId) {
      await this.openChat(threadId, { syncUrl: false });
      return;
    }

    if (!userId || !realtyId) {
      return;
    }

    const createResponse = await firstValueFrom(this.chatService.createChatFromAd(userId, realtyId));
    await this.loadChats();
    await this.openChat(createResponse.chat_id, { syncUrl: false });

    if (this.embeddedDesktop()) {
      await this.clearDesktopChatQuery({ chat: '1' });
      return;
    }

    await this.router.navigate(['/chat'], {
      queryParams: { thread: createResponse.chat_id },
      replaceUrl: true,
    });
  }

  private async loadChats(): Promise<void> {
    const response = await firstValueFrom(this.chatService.listChats());
    this.setChats(response.chats ?? []);
  }

  private startRealtime(): void {
    this.realtimeClient?.end(true);
    this.realtimeClient = this.chatService.connectRealtime((topic, payload) => {
      void this.handleRealtimeMessage(topic, payload);
    });
  }

  private async openChat(chatId: number, options: { syncUrl?: boolean } = {}): Promise<void> {
    this.isLoadingThread.set(true);
    this.loadError.set('');

    try {
      const [chatResponse] = await Promise.all([
        firstValueFrom(this.chatService.getChat(chatId)),
        this.loadMessages(chatId),
      ]);

      this.activeChatId.set(chatId);
      this.activeChat.set(chatResponse.chat);
      this.activeChatPriceLabel.set('');

      try {
        const adResponse = await firstValueFrom(
          this.chatService.getAdPreview(chatResponse.chat.realty_id)
        );
        this.activeChatPriceLabel.set(
          this.chatService.formatPriceLabel(adResponse.ad)
        );
      } catch {
        this.activeChatPriceLabel.set('');
      }

      const matchingChat = this.chats().find((item) => item.id === chatId);
      if ((matchingChat?.unread_amount ?? 0) > 0) {
        await firstValueFrom(this.chatService.markViewed(chatId));
        this.setChats(
          this.chats().map((item) =>
            item.id === chatId ? { ...item, unread_amount: 0 } : item
          )
        );
      }

      if (options.syncUrl !== false) {
        await this.syncMobileThreadUrl(chatId);
      }
    } catch {
      this.loadError.set(this.i18n.t('chat.openError'));
    } finally {
      this.isLoadingThread.set(false);
    }
  }

  private async syncMobileThreadUrl(chatId: number): Promise<void> {
    if (this.embeddedDesktop()) {
      return;
    }

    await this.router.navigate(['/chat'], {
      queryParams: { thread: chatId },
    });
  }

  private async loadMessages(chatId: number): Promise<void> {
    const response = await firstValueFrom(this.chatService.getMessages(chatId));
    const userId = this.authService.getAuthenticatedUserId();
    const ordered = [...(response.chat_messages ?? [])].reverse();

    this.messages.set(
      ordered.map((message) => {
        const date = this.toDate(message.timestamp);

        return {
          ...message,
          mine: userId === message.user_id,
          displayDate: this.formatDate(date),
          displayTime: this.formatTime(date),
        };
      })
    );
  }

  private async handleRealtimeMessage(
    topic: string,
    payload: ChatRealtimeMessage | null
  ): Promise<void> {
    if (!topic.includes('chat/message/add') || !payload) {
      return;
    }

    const chatId = payload.chat_id;
    const currentUserId = this.authService.getAuthenticatedUserId();
    const chatItem = this.chats().find((item) => item.id === chatId);

    if (!chatItem) {
      await this.loadChats();
      return;
    }

    if (this.activeChatId() !== chatId || payload.user_id === currentUserId) {
      if (payload.user_id !== currentUserId) {
        this.setChats(
          this.chats().map((item) =>
            item.id === chatId
              ? { ...item, unread_amount: (item.unread_amount ?? 0) + 1 }
              : item
          )
        );
      }
      return;
    }

    if (this.messages().some((message) => message.id === payload.message_id)) {
      return;
    }

    const date = this.toDate(payload.timestamp);
    this.messages.set([
      ...this.messages(),
      {
        avatar: '',
        displayDate: this.formatDate(date),
        displayTime: this.formatTime(date),
        id: payload.message_id,
        media: payload.media,
        mine: false,
        status: payload.status,
        text: payload.text ?? '',
        timestamp: payload.timestamp,
        user_id: payload.user_id,
        username: '',
      },
    ]);

    queueMicrotask(() => {
      const container = document.querySelector('.chat-messages-container');
      if (container instanceof HTMLElement) {
        container.scrollTop = container.scrollHeight;
      }
    });
  }

  private toDate(timestamp: number): Date {
    return new Date(timestamp / 1000000);
  }

  private formatDate(date: Date): string {
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
  }

  private formatTime(date: Date): string {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  private setChats(chats: ChatListItem[]): void {
    this.chats.set(chats);
    this.chatService.setUnreadCountFromChats(chats);
  }

  private clearDesktopChatQuery(keep: Record<string, string> = {}): Promise<boolean> {
    const [pathOnly, queryString = ''] = this.router.url.split('?');
    const params = new URLSearchParams(queryString);

    ['chat', 'uId', 'rId'].forEach((key) => params.delete(key));

    Object.entries(keep).forEach(([key, value]) => params.set(key, value));

    const nextQuery = params.toString();
    return this.router.navigateByUrl(nextQuery ? `${pathOnly}?${nextQuery}` : pathOnly, {
      replaceUrl: true,
    });
  }
}
